import React, { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  StyledClinAnnounceBar,
  StyledCloseIcon,
  StyledMessage
} from './ClinAnnounceBar.styles'
import { ClinTheme } from '../../ClinTheme'
import { ClinIcon } from '../ClinIcon'
import { ClinIconPathName } from '../ClinIcon/ClinIcon.paths'
import { ClinText } from '../ClinText'

export enum AnnounceMode {
  Success = 'Success',
  Information = 'Information',
  Warning = 'Warning',
  Error = 'Error'
}

interface IAnnounce {
  /** The translation key used for retrieving content from squidex */
  transKey: string
}

/**
 * Look up record/table for returning an announce title value
 * @usage:
 *
 * const { transKey } = announceModeRecord[AnnounceMode.Success]
 *
 */
export const announceModeRecord: Record<AnnounceMode, IAnnounce> = {
  [AnnounceMode.Success]: {
    transKey: 'announce_mode:success'
  },
  [AnnounceMode.Information]: {
    transKey: 'announce_mode:information'
  },
  [AnnounceMode.Warning]: {
    transKey: 'announce_mode:warning'
  },
  [AnnounceMode.Error]: {
    transKey: 'announce_mode:error'
  }
}

export interface IClinAnnounceBarProps {
  /** The title of the announce - defaults to the mode */
  title?: string
  /** The message of the announce */
  message?: string
  /** The mode of the announce */
  mode?: AnnounceMode
  /** Determines if the close x is displayed */
  isDismissible?: boolean
  /** Determines if the dismiss button is displayed */
  isDismissibleText?: boolean
  /** Add bottom margin */
  marginBottom?: number
  marginTop?: number
  /** The mode of the announce */
  handleClose?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  className?: string
  textClassName?: string
  iconSize?: number
  iconFill?: string
  messageClassName?: string
  iconName?: ClinIconPathName
  viewBox?: string
  fillRule?: 'evenodd' | 'nonzero' | 'inherit'
  clipRule?: string
}

export const getIconNameForMode = (mode: AnnounceMode): ClinIconPathName => {
  switch (mode) {
    case AnnounceMode.Information:
      return ClinIconPathName.InfoOutline
    case AnnounceMode.Success:
      return ClinIconPathName.CheckOutline
    case AnnounceMode.Error:
      return ClinIconPathName.ExclamationOutline
    default:
      return ClinIconPathName.ExclamationOutline
  }
}

export const ClinAnnounceBar: FunctionComponent<IClinAnnounceBarProps> = ({
  children,
  iconName,
  className,
  messageClassName,
  title,
  message,
  textClassName,
  mode = AnnounceMode.Error,
  isDismissible,
  marginBottom,
  marginTop,
  handleClose,
  iconSize,
  iconFill,
  viewBox,
  fillRule,
  clipRule
}) => {
  const { t } = useTranslation()
  return (
    <StyledClinAnnounceBar
      mode={mode}
      marginBottom={marginBottom}
      marginTop={marginTop}
      className={`clin-announce ${className}`}
      data-testid="clin-announce"
      style={mode === AnnounceMode.Information ? {} : { alignItems: 'center' }}
    >
      <ClinIcon
        fillRule={
          fillRule ??
          (mode === AnnounceMode.Information ? 'evenodd' : 'nonzero')
        }
        clipRule={clipRule}
        iconSize={iconSize}
        iconFill={iconFill}
        viewBox={viewBox}
        iconName={iconName ?? getIconNameForMode(mode)}
        style={{
          marginLeft: ClinTheme.space[2]
        }}
      />
      <StyledMessage className={messageClassName}>
        <ClinText
          as="div"
          fontWeight={ClinTheme.fontWeights.bold}
          color={mode === AnnounceMode.Information ? 'black' : 'inherit'}
        >
          {title ?? t(`${announceModeRecord[mode].transKey}`)}
        </ClinText>
        <ClinText
          as="div"
          color={mode === AnnounceMode.Information ? 'black' : 'inherit'}
          className={textClassName}
        >
          {children || message}
        </ClinText>
      </StyledMessage>
      {isDismissible && (
        <StyledCloseIcon onClick={(event) => handleClose?.(event)}>
          <ClinIcon iconName={ClinIconPathName.Cross} />
        </StyledCloseIcon>
      )}
    </StyledClinAnnounceBar>
  )
}
